import type { Child, ChildObservation, ChildrenResult, ChildrenQuery } from '@partou/my-group/data';
import { Injectable } from '@angular/core';
import { Observable, of, switchMap, throwError, timer } from 'rxjs';
import {
  MOCK_CHILDREN_SCENARIOS,
  applyChildrenQuery,
  buildMockCreatedObservation,
  buildMockReportPdf,
  getCreatedObservations,
  getMockChildrenCreateOutcome,
  getMockChildrenDelayMs,
  getMockChildrenDeleteOutcome,
  getMockChildrenHideOutcome,
  getMockChildrenLoadOutcome,
  getMockChildrenReportOutcome,
  getMockChildrenScenarioName,
} from '../constants/mock-children.const';

@Injectable()
export class MockChildrenApiService {
  getChildren(query: ChildrenQuery): Observable<ChildrenResult> {
    const delayMs: number = getMockChildrenDelayMs();
    const source: Observable<ChildrenResult> =
      getMockChildrenLoadOutcome() === 'error'
        ? throwError((): Error => new Error('Failed to load children'))
        : of(applyChildrenQuery(MOCK_CHILDREN_SCENARIOS[getMockChildrenScenarioName()], query));
    return delayMs > 0 ? timer(delayMs).pipe(switchMap((): Observable<ChildrenResult> => source)) : source;
  }

  getChild(id: string): Observable<Child> {
    const child: Child | undefined = MOCK_CHILDREN_SCENARIOS[getMockChildrenScenarioName()].find(
      (candidate: Child): boolean => candidate.id === id,
    );
    if (child === undefined) return throwError((): Error => new Error(`Child ${id} not found`));
    return of({ ...child, observations: [...child.observations, ...getCreatedObservations(id)] });
  }

  deleteNote(): Observable<void> {
    return this.simulateDelete();
  }

  deleteObservation(): Observable<void> {
    return this.simulateDelete();
  }

  createObservation(childId: string): Observable<ChildObservation> {
    const delayMs: number = getMockChildrenDelayMs();
    const source: Observable<ChildObservation> =
      getMockChildrenCreateOutcome() === 'error'
        ? throwError((): Error => new Error('Create rejected'))
        : of(buildMockCreatedObservation(childId));
    return delayMs > 0 ? timer(delayMs).pipe(switchMap((): Observable<ChildObservation> => source)) : source;
  }

  updateChildHidden(childId: string, hidden: boolean): Observable<Child> {
    const delayMs: number = getMockChildrenDelayMs();
    const source: Observable<Child> = this.hideSource(childId, hidden);
    return delayMs > 0 ? timer(delayMs).pipe(switchMap((): Observable<Child> => source)) : source;
  }

  generateParentReport(): Observable<Blob> {
    const delayMs: number = getMockChildrenDelayMs();
    const source: Observable<Blob> =
      getMockChildrenReportOutcome() === 'error'
        ? throwError((): Error => new Error('Report generation rejected'))
        : of(buildMockReportPdf());
    return delayMs > 0 ? timer(delayMs).pipe(switchMap((): Observable<Blob> => source)) : source;
  }

  private hideSource(childId: string, hidden: boolean): Observable<Child> {
    if (getMockChildrenHideOutcome() === 'error') return throwError((): Error => new Error('Hide rejected'));
    const child: Child | undefined = MOCK_CHILDREN_SCENARIOS[getMockChildrenScenarioName()].find(
      (candidate: Child): boolean => candidate.id === childId,
    );
    if (child === undefined) return throwError((): Error => new Error(`Child ${childId} not found`));
    return of({ ...child, hidden });
  }

  private simulateDelete(): Observable<void> {
    return getMockChildrenDeleteOutcome() === 'error'
      ? throwError((): Error => new Error('Delete rejected'))
      : of(undefined);
  }
}
